<template>
  <div>
    <LoadingSpinner :isLoading="isLoading" />
    <modal name="update-alarm-contract" class="modal-inner modal-update-alarm-contract" transition="pop-out" :width="modalWidth" :focus-trap="true" :min-height="600" height="auto" :scrollable="true">
      <span class="close-button" @click="hide('update-alarm-contract')"><BaseIcon icon="times-circle" class="text-white"/></span>
      <div class="modal-header text-lg bg-primary-50 font-serif">
        <span v-if="isBulk"> {{ $t("bulk_alarm_update.editor_title", { count: bulkAlarms.length }) }} </span>
        <span v-else-if="updateInfo"> {{ $t("change_alarm_plan.change_alarm_plan") }} - {{ updateInfo.unit_name }} </span>
      </div>
      <div class="modal-body" :class="{ 'bulk-update-tint': isBulk }">
        <ValidationObserver ref="formUpdateAlarmContract" v-if="(updateInfo != null || isBulk) && (!isBulk || bulkResults == null)">
          <!-- ===== SEC-218 v2 empty bulk form: every field starts blank; only
               the fields support fills in are written to all selected devices ===== -->
          <div v-if="isBulk">
            <div class="px-2 mb-4">
              <p class="text-sm font-semibold mb-1">{{ $t("bulk_alarm_update.devices_being_updated") }}</p>
              <table class="w-full text-xs border border-gray-200">
                <thead>
                  <tr class="bg-gray-100 text-left">
                    <th class="px-2 py-1">{{ $t("change_alarm_plan.product_name") }}</th>
                    <th class="px-2 py-1">{{ $t("change_alarm_plan.telematics_number") }}</th>
                    <th class="px-2 py-1">{{ $t("change_alarm_plan.agreement_number") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="a in bulkAlarms" :key="a._id" class="border-t border-gray-200">
                    <td class="px-2 py-1">{{ a.unit_name }}</td>
                    <td class="px-2 py-1">{{ a.phone }}</td>
                    <td class="px-2 py-1">{{ a.agreement_number || "-" }}</td>
                  </tr>
                </tbody>
              </table>
              <p class="text-xs italic text-gray-600 mt-2">{{ $t("bulk_alarm_update.empty_hint") }}</p>
            </div>

            <!-- alarm central / device status / agreement number -->
            <div class="flex flex-wrap">
              <div class="w-full md:w-1/3 px-2" v-if="user.role != 'superuser' && user.role != 'user'">
                <BaseSelect v-model="bulkForm.alarm_central" :options="bulkAlarmCentralOptions" nameSelector="name" optionSelector="_id" :field_name="$t('change_alarm_plan.alarm_center')" :readonly="user.role != 'admin' && user.role != 'seller'" :disabled="user.role != 'admin' && user.role != 'seller'" />
              </div>
              <div class="w-full md:w-1/3 px-2" v-if="user.role == 'admin' || user.role == 'seller'">
                <BaseSelect v-model="bulkForm.device_status" :options="bulkDeviceStatusOptions" nameSelector="label" optionSelector="value" :field_name="$t('device_status.label')" />
              </div>
              <div class="w-full md:w-1/3 px-2" v-if="user.role == 'admin'">
                <BaseInput v-model.trim="bulkForm.agreement_number" type="text" :field_name="$t('change_alarm_plan.agreement_number')" />
              </div>
              <!-- seller (2026-09-08): same list as the single-device form; "no change" leaves every device's seller alone -->
              <div class="w-full md:w-1/3 px-2" v-if="user.role == 'admin' || user.role == 'seller'">
                <BaseSelect v-model="bulkForm.seller" :options="bulkSellerOptions" nameSelector="name" optionSelector="_id" :field_name="$t('change_alarm_plan.seller')" />
              </div>
              <div class="w-full md:w-1/3 px-2">
                <BaseSwitch v-model="bulkForm.man_down" :field_name="$t('change_alarm_plan.man_down')" :readonly="user.role != 'admin' && user.role != 'seller'" :disabled="user.role != 'admin' && user.role != 'seller'" />
              </div>
              <div class="w-full md:w-1/3 px-2" v-if="bulkShowDirectDispatch">
                <!-- SEC-373: no change / on / off - a switch could only turn guard dispatch on -->
                <BaseSelect v-model="bulkForm.direct_dispatch" :options="bulkDirectDispatchOptions" nameSelector="label" optionSelector="value" :field_name="$t('change_alarm_plan.direct_dispatch')" :readonly="user.role != 'admin' && user.role != 'seller'" :disabled="user.role != 'admin' && user.role != 'seller'" />
              </div>
            </div>

            <div class="mx-2 border-b border-secondary-400 my-5"></div>

            <!-- alarm type / risk profile / users / reference -->
            <div class="flex flex-wrap">
              <div class="w-full md:w-1/3 px-2">
                <BaseSelect v-model="bulkForm.selected_alarm_sort" :options="safe4AlarmSortChoices(bulkAlarmSortOptions, bulkAnyOnSafe4)" nameSelector="label" optionSelector="value" :field_name="$t('change_alarm_plan.alarm_type')" />
              </div>
              <div class="w-full md:w-1/3 px-2">
                <BaseSelect v-model="bulkForm.risk_profile" :options="bulkRiskProfileOptions" nameSelector="label" optionSelector="value" :field_name="$t('change_alarm_plan.risk_profile')" :readonly="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller'" :disabled="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller'" />
              </div>
              <div class="w-full md:w-1/3 px-2">
                <BaseInput v-model="bulkForm.user" type="number" :field_name="$t('change_alarm_plan.number_of_users')" />
              </div>
              <div class="w-full md:w-1/3 px-2">
                <BaseInput v-model="bulkForm.reference_number" type="text" :field_name="$t('change_alarm_plan.reference_number')" />
              </div>
            </div>

            <!-- personal alarm (only when personal alarm type is chosen) -->
            <!-- SEC-364 A16: no personal alarm for Safe-4 -->
            <div class="flex flex-wrap" v-if="bulkShowPersonalAlarm && !bulkAnyOnSafe4">
              <div class="w-full md:w-1/3 px-2">
                <BaseInput v-model="bulkForm.personal_alarm.reporting" type="text" :field_name="$t('change_alarm_plan.signal_element')" />
              </div>
              <div class="w-full md:w-1/3 px-2">
                <BaseInput v-model="bulkForm.personal_alarm.name" type="text" :field_name="$t('change_alarm_plan.name')" />
              </div>
              <div class="w-full md:w-1/3 px-2">
                <BaseInput v-model="bulkForm.personal_alarm.home_address" type="text" :field_name="$t('change_alarm_plan.home_address')" />
              </div>
              <div class="w-full md:w-1/3 px-2">
                <BaseSelect v-model="bulkForm.personal_alarm.transport" :options="transportOptions" nameSelector="label" optionSelector="value" :field_name="$t('change_alarm_plan.transport')" />
              </div>
              <div class="w-full md:w-1/3 px-2" v-if="bulkForm.personal_alarm.transport == 'car'">
                <BaseInput v-model="bulkForm.personal_alarm.registration_number" type="text" :field_name="$t('change_alarm_plan.registration_number')" />
              </div>
              <div class="w-full md:w-1/3 px-2" v-if="bulkForm.personal_alarm.transport == 'car'">
                <BaseInput v-model="bulkForm.personal_alarm.model" type="text" :field_name="$t('change_alarm_plan.model')" />
              </div>
              <div class="w-full md:w-1/3 px-2" v-if="bulkForm.personal_alarm.transport == 'car'">
                <BaseInput v-model="bulkForm.personal_alarm.color" type="text" :field_name="$t('change_alarm_plan.color')" />
              </div>
            </div>

            <div class="mx-2 border-b border-secondary-400 my-5"></div>

            <!-- business name / based-on address / work assignment -->
            <div class="flex flex-wrap">
              <div class="w-full md:w-1/3 px-2">
                <BaseInput v-model="bulkForm.alarm_unit_name" type="text" :field_name="$t('change_alarm_plan.business_name')" />
              </div>
              <template v-if="bulkAllOnSafe4">
                <div class="w-full md:w-1/3 px-2">
                  <BaseInput v-model="bulkForm.from_address.street" type="text" rules="max:30" maxlength="30" :field_name="$t('safe4_form.street')" />
                </div>
                <div class="w-full md:w-1/3 px-2">
                  <BaseInput v-model="bulkForm.from_address.zip" type="text" :rules="bulkSafe4PostcodeRules" :field_name="$t('safe4_form.postal_code')" />
                </div>
                <div class="w-full md:w-1/3 px-2">
                  <BaseInput v-model="bulkForm.from_address.city" type="text" :field_name="$t('safe4_form.city')" />
                </div>
              </template>
              <div class="w-full md:w-1/3 px-2" v-else>
                <BaseInput v-model="bulkForm.from_address.city" type="text" :field_name="$t('change_alarm_plan.based_on_address')" />
              </div>
              <div class="w-full px-2">
                <BaseTextarea rows="4" v-model="bulkForm.business_description" :field_name="$t('change_alarm_plan.work_assignment')" />
              </div>
            </div>

            <!-- passwords -->
            <div class="flex flex-wrap">
              <!-- demo feedback: same 10-character cap and hint as the
                   create-device page -->
              <div class="w-full md:w-1/3 px-2">
                <BaseInput v-model="bulkForm.alarm_password" type="text" :rules="bulkAllOnSafe4 ? 'max:20|safe4_password' : ''" :field_name="$t('change_alarm_plan.password')" :maxlength="bulkAllOnSafe4 ? 20 : 200" />
                <p class="text-xs italic text-gray-500 mt-1" v-if="!bulkAllOnSafe4">{{ $t('admin_order_alarm.password_hint') }}</p>
              </div>
              <!-- SEC-364 A5: KUIS has no personal password -->
              <div class="w-full md:w-1/3 px-2" v-if="!bulkAllOnSafe4">
                <BaseInput v-model="bulkForm.personal_password" type="text" :field_name="$t('change_alarm_plan.personal_password')" maxlength="200" />
                <p class="text-xs italic text-gray-500 mt-1">{{ $t('admin_order_alarm.password_hint') }}</p>
              </div>
            </div>

            <div class="mx-2 border-b border-secondary-400 my-5"></div>

            <!-- external contractor -->
            <div class="flex flex-wrap">
              <div class="w-full text-xs italic px-2 mb-2">{{ $t("change_alarm_plan.external_agent") }}</div>
              <div class="w-full md:w-1/3 px-2">
                <BaseInput v-model="bulkForm.external_contractor.name" type="text" :field_name="$t('change_alarm_plan.name')" />
              </div>
              <div class="w-full md:w-1/3 px-2">
                <BaseInput v-model="bulkForm.external_contractor.phone" type="text" :field_name="$t('change_alarm_plan.contact_number')" />
              </div>
              <div class="w-full md:w-1/3 px-2">
                <BaseInput v-model="bulkForm.external_contractor.object_id" type="text" :field_name="$t('change_alarm_plan.id_perpetrator')" />
              </div>
            </div>

            <div class="mx-2 border-b border-secondary-400 my-5"></div>

            <!-- Contact persons, patched slot by slot: row 1 overwrites contact
                 person 1 on every device, row 2 contact person 2, and a row
                 left empty leaves that device's contact person untouched. The
                 rows are numbered so it is visible which slot is being written. -->
            <div class="flex flex-wrap">
              <div class="w-full text-sm font-bold px-2 mb-2">{{ $t("change_alarm_plan.contact_person") }}</div>
              <div class="w-full px-2 text-xs italic text-gray-600 mb-2">{{ $t("bulk_alarm_update.contact_hint") }}</div>
              <!-- Safe-4 (SEC-364 A4): the contact persons are called around the clock -->
              <div class="w-full px-2 text-xs italic mb-1" v-if="bulkAllOnSafe4">
                {{ $t("safe4_form.around_clock") }}
              </div>
              <div class="w-full flex flex-wrap items-center mb-1" v-for="(c, i) in bulkForm.contact_persons" :key="'bulk-contact-' + i">
                <div class="w-full px-2 text-xs font-semibold text-gray-600">{{ $t("bulk_alarm_update.contact_row", { index: i + 1 }) }}</div>
                <div class="w-full md:w-3/12 px-2">
                  <BaseInput v-model="c.name" type="text" :rules="bulkAllOnSafe4 ? 'max:50' : ''" :maxlength="bulkAllOnSafe4 ? 50 : undefined" :placeholder="$t('change_alarm_plan.name')" />
                </div>
                <div class="w-full md:w-3/12 px-2">
                  <BaseInput v-model="c.function" type="text" :rules="bulkAllOnSafe4 ? 'max:40' : ''" :maxlength="bulkAllOnSafe4 ? 40 : undefined" :placeholder="$t('change_alarm_plan.function')" />
                </div>
                <div class="w-full md:w-3/12 px-2">
                  <BasePhoneInput v-model="c.phone" :vid="'contact-phone-' + i" :show_country="bulkAllOnSafe4" :rules="bulkSafe4PhoneRules(i)" :maxlength="bulkAllOnSafe4 ? 20 : undefined" :placeholder="$t('change_alarm_plan.number')" />
                </div>
                <div class="w-full md:w-1/12 px-2">
                  <BaseInput v-model="c.weekday" type="text" :disabled="bulkAllOnSafe4" :placeholder="$t('change_alarm_plan.everyday')" />
                </div>
                <div class="w-full md:w-1/12 px-2">
                  <BaseInput v-model="c.saturday" type="text" :disabled="bulkAllOnSafe4" :placeholder="$t('change_alarm_plan.saturday')" />
                </div>
                <div class="w-full md:w-1/12 px-2">
                  <BaseInput v-model="c.sunday" type="text" :disabled="bulkAllOnSafe4" :placeholder="$t('change_alarm_plan.sun_weekend')" />
                </div>
              </div>
            </div>
          </div>

          <!-- single-device form: wrapped so none of its updateInfo.* refs
               evaluate in bulk mode (updateInfo is null there, SEC-218 v2) -->
          <template v-if="!isBulk">
          <div class="flex flex-wrap">
            <div class="w-full md:w-1/3 px-2">
              <BaseSelect v-model="updateInfo.type" :options="devices" :field_name="$t('change_alarm_plan.device_type')" rules="required" :readonly="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller'" :disabled="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller'" />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="!new RegExp('(SRT334)|(TWIG Beacon)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)">
              <BaseInput v-model.trim="updateInfo.imei_number" type="text" :field_name="$t('change_alarm_plan.imei')" rules="required" :readonly="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller'" :disabled="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller'" />
            </div>
            <!-- SEC-364 A13: the inventory number sits on the top right row, next to
                 the IMEI. This is for every alarm central, not only Safe-4. -->
            <div class="w-full md:w-1/3 px-2" v-if="!new RegExp('(SRT334)|(TWIG Beacon)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)">
              <BaseInput v-model.trim="updateInfo.inventory_number" type="text" :field_name="$t('change_alarm_plan.inventory_number')" :readonly="user.role !== 'admin' && user.role !== 'seller' && user.role !== 'reseller' && user.role !== 'superuser' && user.role !== 'user'" :disabled="user.role !== 'admin' && user.role !== 'seller' && user.role !== 'reseller' && user.role !== 'superuser' && user.role !== 'user'" />
            </div>
          </div>
          <div class="flex flex-wrap" v-if="!isBulk">
            <div class="w-full md:w-1/3 px-2" v-if="!new RegExp('(SRT334)|(TWIG Beacon)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX (?!Hub))').test(updateInfo.type)">
              <BaseInput v-model.trim="updateInfo.phone" type="text" :field_name="$t('change_alarm_plan.telematics_number')" rules="required" :readonly="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller'" :disabled="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller'" />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="!new RegExp('(Avigilon Alta Compact Dome 5MP)').test(updateInfo.type)">
              <BaseInput v-model.trim="updateInfo.modelnumber" type="text" :field_name="$t('change_alarm_plan.serial_number')" rules="required" :readonly="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller'" :disabled="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller'" />
            </div>

            <div class="w-full md:w-1/3 px-2" v-if="!new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX (?!Hub))').test(updateInfo.type)">
              <BaseSelect v-model="updateInfo.operator" :options="sim_operators" :field_name="$t('change_alarm_plan.sim_operator')" rules="required" :readonly="user.role != 'admin' && user.role != 'seller'" :disabled="user.role != 'admin' && user.role != 'seller'" />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5"></div>

          <div class="flex flex-wrap" v-if="!new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)">
            <div class="w-full md:w-1/3 px-2" v-if="user.role != 'superuser' && user.role != 'user'">
              <BaseSelect v-model="updateInfo.alarm_central" :options="[{ name: $t('change_alarm_plan.none'), _id: '-' }, ...alarmCentrals]" nameSelector="name" optionSelector="_id" :field_name="$t('change_alarm_plan.alarm_center')" :rules="updateInfo.type === 'SRT430' && updateInfo.send_to_alarm_center ? srt430AlarmCentralRules : 'required'" @change="onAlarmCentralChange()" :readonly="user.role != 'admin' && user.role != 'seller'" :disabled="user.role != 'admin' && user.role != 'seller'" />
              <!-- Safe-4 registration state (req 1: badge lives here, under the alarm-centre option) -->
              <div v-if="safe4Badge" class="mt-1">
                <span v-if="safe4Badge == 'registered'" class="inline-block px-2 py-0.5 text-xs font-semibold rounded text-green-700 bg-green-100" :title="$t('safe4_status.registered') + ' — TransNR ' + updateInfo.safe4_trans_nr">Safe-4 ✓</span>
                <span v-else class="inline-block px-2 py-0.5 text-xs font-semibold rounded text-yellow-700 bg-yellow-100" :title="$t('safe4_status.pending')">Safe-4 …</span>
              </div>
              <!-- SEC-337: IMSI number, directly under the alarm centre it belongs to.
                   Only for Securitas Sverige AB, and never in the bulk editor (an IMSI
                   belongs to one SIM card, so it cannot be set on many devices at once). -->
              <div class="mt-2" v-if="showImsiNumber && !isBulk">
                <BaseInput v-model="updateInfo.imsi_number" type="text" :field_name="$t('change_alarm_plan.imsi_number')" />
              </div>
            </div>
            <div class="w-full md:w-1/3 px-2" v-else>
              <BaseInput value="OK" type="text" :field_name="$t('change_alarm_plan.alarm_center')" :readonly="true" :disabled="true" />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="show_alarm_number && !isBulk">
              <label class="text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t('change_alarm_plan.alarm_number') }}<b class="text-red-500 ml-1">*</b></label>
              <div class="flex">
                <input 
                  v-model.trim="updateInfo.alarm_number" 
                  type="text" 
                  :readonly="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller'" 
                  :disabled="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller'" 
                  class="bg-gray-100 text-sm text-gray-900 focus:outline-none border-b-4 border-gray-300 hover:border-gray-400 transition duration-500 px-3 pt-2 pb-2 disabled:opacity-50 flex-grow rounded-l"
                />
                <button 
                  v-if="isNorwegianSite && (user.role == 'admin' || user.role == 'seller' || user.role == 'reseller')"
                  type="button"
                  @click="regenerateAlarmNumberFromSerial"
                  class="px-3 bg-primary-500 hover:bg-primary-600 text-white rounded-r border-b-4 border-primary-600 transition-colors"
                  :title="$t('change_alarm_plan.regenerate_alarm_number')"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseSwitch v-model="updateInfo.man_down" :field_name="$t('change_alarm_plan.man_down')" :readonly="user.role != 'admin' && user.role != 'seller'" :disabled="user.role != 'admin' && user.role != 'seller'" />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="showDirectDispatch">
              <BaseSwitch v-model="updateInfo.direct_dispatch" :field_name="$t('change_alarm_plan.direct_dispatch')" :readonly="user.role != 'admin' && user.role != 'seller'" :disabled="user.role != 'admin' && user.role != 'seller'" />
            </div>
            <!-- SEC-339: the security company is entered only under "Extern åtgörare"
                 below, which is the one the PDF prints -->
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5" v-if="!new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)"></div>

          <!-- SEC-364 A14: one row container, so cash agreement, unit status and the
               agreement fields fill two rows instead of three. All alarm centrals. -->
          <div class="flex flex-wrap mb-5" v-if="!isBulk">
            <div class="w-full md:w-1/3 px-2" v-if="user.role == 'admin'">
              <label class="inline-flex items-center mt-3">
                <input v-model="updateInfo.cash_agreement" :value="updateInfo.cash_agreement" type="checkbox" class="form-checkbox h-4 w-4" @change="switchAgreementType()" />
                <span class="ml-2 text-sm font-sans font-semibold">{{ $t("change_alarm_plan.cash_agreement") }}</span>
              </label>
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="user.role == 'admin' || user.role == 'seller'">
              <label class="text-gray-600 text-sm font-bold pb-2 font-serif block">{{ $t('device_status.label') }}</label>
              <select v-model="updateInfo.device_status" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-4 border-gray-300 transition duration-500 px-3 pt-2 pb-2 hover:border-gray-400">
                <option value="ny_bestallning">{{ $t('device_status.ny_bestallning') }}</option>
                <option value="utbytesenhet">{{ $t('device_status.utbytesenhet') }}</option>
                <option value="ersattningsprodukt">{{ $t('device_status.ersattningsprodukt') }}</option>
              </select>
            </div>
            <!-- SEC-334: agreement number, length and start date are not shown to customers of a white label reseller -->
            <div class="w-full md:w-1/3 px-2" v-if="updateInfo.cash_agreement && !whiteLabelCustomer">
              <BaseInput v-model="updateInfo.agreement_number" type="text" :field_name="$t('change_alarm_plan.agreement_number')" readonly disabled />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="updateInfo.cash_agreement && !whiteLabelCustomer">
              <BaseInput v-model.trim="updateInfo.agreement_length" type="text" :field_name="$t('change_alarm_plan.contract_length')" readonly disabled />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="!updateInfo.cash_agreement && !whiteLabelCustomer">
              <BaseInput v-model.trim="updateInfo.agreement_number" type="text" :field_name="$t('change_alarm_plan.agreement_number')" rules="required" :readonly="user.role != 'admin'" :disabled="user.role != 'admin'" :placeholder="agreementNumberMask" />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="!updateInfo.cash_agreement && !whiteLabelCustomer">
              <BaseInput v-model.trim="updateInfo.agreement_length" type="text" :field_name="$t('change_alarm_plan.contract_length')" rules="required" :readonly="user.role != 'admin' && user.role != 'seller'" :disabled="user.role != 'admin' && user.role != 'seller'" />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="!whiteLabelCustomer">
              <ValidationProvider name="Startdatum" rules="required" v-slot="{ classes, errors }">
                <div class="input-validate" :class="classes">
                  <label class="text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t("change_alarm_plan.start_date") }}<b class="text-red-500 ml-1">*</b></label>
                  <date-picker class="mt-2" :lang="datePickerLocale" :key="date_picker_langKey" v-model="updateInfo.contract_start" value-type="format" format="YYYY-MM-DD" :readonly="user.role != 'admin' && user.role != 'seller'" :disabled="user.role != 'admin' && user.role != 'seller'"></date-picker>
                  <span>{{ errors[0] }}</span>
                </div>
              </ValidationProvider>
            </div>
            <!-- SEC-334: the seller (a Securdia seller's name) is not shown to customers of a white label reseller -->
            <div class="w-full md:w-1/3 px-2" v-if="!whiteLabelCustomer">
              <BaseSelect v-model="updateInfo.seller" :options="sellers" nameSelector="name" optionSelector="_id" :field_name="$t('change_alarm_plan.seller')" :readonly="user.role != 'admin' && user.role != 'seller'" :disabled="user.role != 'admin' && user.role != 'seller'" @change="check_contract_party()" />
            </div>
            <!-- SEC-321: Avtalspart (contract party) — only for Westra + Commuter Security Group AB (CSG) -->
            <div class="w-full md:w-1/3 px-2" v-if="showContractParty">
              <BaseSelect v-model="contractPartyChoice" :options="[{ name: 'Commuter Security Group AB (CSG)', value: 'Commuter Security Group AB (CSG)' }, { name: $t('change_alarm_plan.none'), value: 'none' }]" nameSelector="name" optionSelector="value" :field_name="$t('change_alarm_plan.avtalspart')" :readonly="user.role != 'admin' && user.role != 'seller'" :disabled="user.role != 'admin' && user.role != 'seller'" />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5"></div>

          <div class="flex flex-wrap">
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="updateInfo.delivery_company.name" type="text" :field_name="$t('change_alarm_plan.business')" readonly disabled />
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="updateInfo.delivery_company.phone" type="text" :field_name="$t('change_alarm_plan.phone')" readonly disabled />
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="updateInfo.delivery_company.email" type="text" :field_name="$t('change_alarm_plan.email')" readonly disabled />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5"></div>

          <div class="flex flex-wrap" v-if="!new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)">
            <div class="w-full md:w-1/3 px-2">
              <BaseSelect v-model="updateInfo.selected_alarm_sort" :options="safe4AlarmSortChoices(alarmSortOptions, isSafe4Selected)" nameSelector="label" optionSelector="value" :field_name="$t('change_alarm_plan.alarm_type')" rules="required" @change="check_personal_alarm()" />
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseSelect v-model="updateInfo.risk_profile" :options="riskProfileOptions" nameSelector="label" optionSelector="value" rules="required" :field_name="$t('change_alarm_plan.risk_profile')" :readonly="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller'" :disabled="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller'" v-if="!show_MSL_risk"/>
              <BaseSelect v-model="updateInfo.risk_profile" :options="riskProfileOptionsMSL" nameSelector="label" optionSelector="value" rules="required" :field_name="$t('change_alarm_plan.risk_profile')" :readonly="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller'" :disabled="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller'" v-if="show_MSL_risk"/>
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="updateInfo.user" type="number" rules="required" :field_name="$t('change_alarm_plan.number_of_users')" />
            </div>
          </div>

          <div class="flex flex-wrap" v-if="isBurglarAlarm || isThreatAlarm">
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="updateInfo.reference_number" type="text" :field_name="$t('change_alarm_plan.reference_number')" />
            </div>
          </div>

          <div class="flex flex-wrap" v-if="show_personal_alarm && !isSafe4Selected && !new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)">
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="updateInfo.personal_alarm.reporting" type="text" :field_name="$t('change_alarm_plan.signal_element')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="updateInfo.personal_alarm.name" type="text" :field_name="$t('change_alarm_plan.name')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="updateInfo.personal_alarm.home_address" type="text" :field_name="$t('change_alarm_plan.home_address')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseSelect v-model="updateInfo.personal_alarm.transport" :options="transportOptions" nameSelector="label" optionSelector="value" :field_name="$t('change_alarm_plan.transport')" />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="updateInfo.personal_alarm.transport == 'car'">
              <BaseInput v-model="updateInfo.personal_alarm.registration_number" type="text" :field_name="$t('change_alarm_plan.registration_number')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="updateInfo.personal_alarm.transport == 'car'">
              <BaseInput v-model="updateInfo.personal_alarm.model" type="text" :field_name="$t('change_alarm_plan.model')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="updateInfo.personal_alarm.transport == 'car'">
              <BaseInput v-model="updateInfo.personal_alarm.color" type="text" :field_name="$t('change_alarm_plan.color')" rules="required" />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5" v-if="!new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)"></div>

          <div class="flex flex-wrap">
            <div class="w-full md:w-1/3 px-2" v-if="!isBulk">
              <BaseInput v-model="updateInfo.unit_name" type="text" rules="required" :field_name="$t('change_alarm_plan.product_name')" />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="!isSafe4Selected && !new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)">
              <BaseInput v-model="updateInfo.from_address.city" type="text" rules="required" :field_name="$t('change_alarm_plan.based_on_address')" />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="!new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)">
              <BaseInput v-model="updateInfo.alarm_unit_name" type="text" rules="required" :field_name="$t('change_alarm_plan.business_name')" />
            </div>
            <!-- Safe-4 (SEC-364 A12): street, postal code and city belong together,
                 so they get a row of their own instead of wrapping across two.
                 Splitting them also keeps the KUIS postkode a clean value (req 8). -->
            <template v-if="isSafe4Selected">
              <div class="w-full flex flex-wrap">
                <div class="w-full md:w-1/3 px-2">
                  <BaseInput v-model="updateInfo.from_address.street" type="text" rules="required|max:30" maxlength="30" :field_name="$t('safe4_form.street')" />
                </div>
                <div class="w-full md:w-1/3 px-2">
                  <BaseInput v-model="updateInfo.from_address.zip" type="text" :rules="safe4PostcodeRules" :field_name="$t('safe4_form.postal_code')" />
                </div>
                <div class="w-full md:w-1/3 px-2">
                  <BaseInput v-model="updateInfo.from_address.city" type="text" rules="required" :field_name="$t('safe4_form.city')" />
                </div>
              </div>
            </template>
            <div class="w-full md:w-1/3 px-2" v-if="!new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)">
              <BaseTextarea rows="5" v-model="updateInfo.business_description" :field_name="$t('change_alarm_plan.work_assignment')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="updateInfo.type == 'SRT330' || updateInfo.type == 'SRT430'">
              <BaseTextarea rows="5" v-model="updateInfo.comment" :field_name="$t('change_alarm_plan.comment')" />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5" v-if="!new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)"></div>

          <div class="flex flex-wrap" v-if="!new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)">
            <div class="w-full text-xs italic px-2 mb-2">
              {{ isSosAlarm ? $t("change_alarm_plan.device_password_sos") : $t("change_alarm_plan.device_password") }}
            </div>
            <div class="w-full md:w-1/3 px-2" v-if="!new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)">
              <BaseInput v-model="updateInfo.alarm_password" vid="alarm-password" type="text" :rules="isSafe4Selected ? 'required|max:20|safe4_password' : 'required'" :field_name="isSosAlarm ? $t('change_alarm_plan.password_sos') : $t('change_alarm_plan.password')" :maxlength="isSafe4Selected ? 20 : (isSosAlarm ? 10 : undefined)" />
            </div>
            <!-- Safe-4 (SEC-364 A5): KUIS has no personal password, so the field is
                 hidden and its value cleared when Safe-4 is the alarm central. -->
            <div class="w-full md:w-1/3 px-2" v-if="!isSafe4Selected && !new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(4G Box)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)">
              <BaseInput v-model="updateInfo.personal_password" type="text" :field_name="isSosAlarm ? $t('change_alarm_plan.personal_password_sos') : $t('change_alarm_plan.personal_password')" :maxlength="isSosAlarm ? 10 : undefined" />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5" v-if="!new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)"></div>

          <div class="flex flex-wrap" v-if="!new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)">
            <div class="w-full text-xs italic px-2 mb-2">
              {{ $t("change_alarm_plan.external_agent") }}
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="updateInfo.external_contractor.name" type="text" :field_name="$t('change_alarm_plan.name')" :readonly="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller' && user.role != 'superuser' && user.role != 'user'" :disabled="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller' && user.role != 'superuser' && user.role != 'user'" />
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="updateInfo.external_contractor.phone" type="text" :field_name="$t('change_alarm_plan.contact_number')" :readonly="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller' && user.role != 'superuser' && user.role != 'user'" :disabled="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller' && user.role != 'superuser' && user.role != 'user'" />
            </div>
            <div class="w-full md:w-1/3 px-2">
              <BaseInput v-model="updateInfo.external_contractor.object_id" type="text" :field_name="$t('change_alarm_plan.id_perpetrator')" :readonly="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller' && user.role != 'superuser' && user.role != 'user'" :disabled="user.role != 'admin' && user.role != 'seller' && user.role != 'reseller' && user.role != 'superuser' && user.role != 'user'" />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5" v-if="!new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)"></div>

          <div class="flex flex-wrap" v-if="!new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)">
            <div class="w-full text-sm font-bold px-2 mb-2">
              {{ $t("change_alarm_plan.contact_person") }}
            </div>
            <div class="w-full text-xs italic px-2 mb-2">
              <p>{{ $t("change_alarm_plan.contact_person_details") }}</p>

              <ol>
                <li>{{ $t("change_alarm_plan.wardenship") }} – 031 703 10 00 - {{ $t("change_alarm_plan.everyday") }} 08-16</li>
                <li>{{ $t("change_alarm_plan.palm_leather") }} – 0799 99 99 99 - {{ $t("change_alarm_plan.around_the_clock") }}</li>
              </ol>
            </div>
            <!-- Safe-4 (SEC-364 A4): the contact persons are called around the clock,
                 so the time fields hold the whole day. The note sits above them, in italics. -->
            <div class="w-full px-2 text-xs italic mb-1" v-if="isSafe4Selected">
              {{ $t("safe4_form.around_clock") }}
            </div>
            <div class="w-full flex flex-wrap" v-if="!new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)">
              <div class="w-full md:w-3/12 px-2">
                <BaseInput v-model="updateInfo.contact_persons[0].name" type="text" :rules="isSafe4Selected ? 'required|max:50' : 'required'" :maxlength="isSafe4Selected ? 50 : undefined" :field_name="$t('change_alarm_plan.name')" :placeholder="$t('change_alarm_plan.name')" />
              </div>
              <div class="w-full md:w-3/12 px-2">
                <BaseInput v-model="updateInfo.contact_persons[0].function" type="text" :rules="isSafe4Selected ? 'required|max:40' : 'required'" :maxlength="isSafe4Selected ? 40 : undefined" :field_name="$t('change_alarm_plan.function')" :placeholder="$t('change_alarm_plan.function')" />
              </div>
              <div class="w-full md:w-3/12 px-2">
                <BasePhoneInput v-model="updateInfo.contact_persons[0].phone" vid="contact-phone-0" :show_country="isSafe4Selected" :rules="safe4PhoneRules(updateInfo.contact_persons, 0, isSafe4Selected, true)" :maxlength="isSafe4Selected ? 20 : undefined" :field_name="$t('change_alarm_plan.number')" :placeholder="$t('change_alarm_plan.number')" />
              </div>
              <div class="w-full md:w-1/12 px-2">
                <BaseInput v-model="updateInfo.contact_persons[0].weekday" type="text" :disabled="isSafe4Selected" :field_name="$t('change_alarm_plan.everyday')" :placeholder="$t('change_alarm_plan.everyday')" />
              </div>
              <div class="w-full md:w-1/12 px-2">
                <BaseInput v-model="updateInfo.contact_persons[0].saturday" type="text" :disabled="isSafe4Selected" :field_name="$t('change_alarm_plan.saturday')" :placeholder="$t('change_alarm_plan.saturday')" />
              </div>
              <div class="w-full md:w-1/12 px-2">
                <BaseInput v-model="updateInfo.contact_persons[0].sunday" type="text" :disabled="isSafe4Selected" :field_name="$t('change_alarm_plan.sun_weekend')" :placeholder="$t('change_alarm_plan.sun_weekend')" />
              </div>
            </div>
            <div class="w-full" v-for="(c, i) in updateInfo.contact_persons" :key="i">
              <div class="flex flex-wrap" v-if="i > 0">
                <div class="w-full md:w-3/12 px-2">
                  <BaseInput v-model="c.name" type="text" :rules="isSafe4Selected ? 'max:50' : ''" :maxlength="isSafe4Selected ? 50 : undefined" :placeholder="$t('change_alarm_plan.name')" />
                </div>
                <div class="w-full md:w-3/12 px-2">
                  <BaseInput v-model="c.function" type="text" :rules="isSafe4Selected ? 'max:40' : ''" :maxlength="isSafe4Selected ? 40 : undefined" :placeholder="$t('change_alarm_plan.function')" />
                </div>
                <div class="w-full md:w-3/12 px-2">
                  <BasePhoneInput v-model="c.phone" :vid="'contact-phone-' + i" :show_country="isSafe4Selected" :rules="safe4PhoneRules(updateInfo.contact_persons, i, isSafe4Selected, false)" :maxlength="isSafe4Selected ? 20 : undefined" :placeholder="$t('change_alarm_plan.number')" />
                </div>
                <div class="w-full md:w-1/12 px-2">
                  <BaseInput v-model="c.weekday" type="text" :disabled="isSafe4Selected" :placeholder="$t('change_alarm_plan.everyday')" />
                </div>
                <div class="w-full md:w-1/12 px-2">
                  <BaseInput v-model="c.saturday" type="text" :disabled="isSafe4Selected" :placeholder="$t('change_alarm_plan.saturday')" />
                </div>
                <div class="w-full md:w-1/12 px-2">
                  <BaseInput v-model="c.sunday" type="text" :disabled="isSafe4Selected" :placeholder="$t('change_alarm_plan.sun_weekend')" />
                </div>
              </div>
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5" v-if="(user.role == 'admin' || user.role == 'seller') && !new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)"></div>

          <div class="flex flex-wrap mt-4" v-if="!new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(^TELTONIKA)|(4G Box)|(Anchor Beacon)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)">
            <!-- Safe-4 (SEC-364 A1): the operator instructions are long and rarely
                 edited, so they sit in a collapsed section. Click the header to open them.
                 Other alarm centrals keep them open, exactly as before. -->
            <div class="w-full px-2" v-if="isSafe4Selected">
              <div class="flex items-center cursor-pointer text-sm font-semibold py-2" @click="show_operator_instructions = !show_operator_instructions">
                <span class="mr-2">{{ $t("change_alarm_plan.operator_instructions") }}</span>
                <BaseIcon icon="angle-down" v-if="!show_operator_instructions" />
                <BaseIcon icon="angle-up" v-else />
              </div>
              <template v-if="show_operator_instructions">
                <!-- the alarm profile exactly as the alarm plan PDF prints it -->
                <AlarmProfilePreview v-if="updateInfo.risk_profile" :risk-profile="updateInfo.risk_profile" />
                <p v-else class="text-xs italic px-2">{{ $t("change_alarm_plan.operator_instructions_no_profile") }}</p>
              </template>
            </div>
            <!-- the alarm profile exactly as the alarm plan PDF prints it -->
            <AlarmProfilePreview v-else-if="updateInfo.risk_profile" :risk-profile="updateInfo.risk_profile" :unit="unitForAlarm(updateInfo)" :customer="customer" />
          </div>
          </template>
        </ValidationObserver>

        <!-- per-device outcome of a bulk update (REQ-01), with retry options.
             Bulk-only: a single-device edit must never show this panel. -->
        <div v-if="isBulk && bulkResults" class="px-2 py-4">
          <p class="text-sm font-bold mb-3">{{ $t("bulk_alarm_update.result_title") }}</p>
          <ul class="mb-3">
            <li v-for="r in bulkResults" :key="r.alarm_id" class="text-sm mb-1">
              <span v-if="r.success" class="text-green-600 font-bold mr-1">&#10003;</span>
              <span v-else class="text-red-600 font-bold mr-1">&#10007;</span>
              {{ r.unit_name || r.alarm_id }}
              <!-- SEC-364 B1: a device Safe-4 refused was left untouched; say why -->
              <span v-if="!r.success && r.reason == 'safe4_rejected'" class="text-red-600 text-xs ml-1">{{ safe4RefusalText(r.safe4) }}</span>
              <span v-else-if="!r.success" class="text-red-600 text-xs ml-1">{{ $t("bulk_alarm_update.result_failed") }}</span>
            </li>
          </ul>
          <p v-if="bulkHasFailures" class="text-sm text-red-600 mb-1">{{ $t("bulk_alarm_update.partial_failure_hint") }}</p>
          <p v-if="bulkEmailFailed" class="text-sm text-red-600">{{ $t("bulk_alarm_update.email_failed") }}</p>
          <p v-else-if="bulkEmailSent" class="text-sm text-green-600">{{ $t("change_alarm_plan.sent_to_alarm_center") }}</p>
          <!-- saved, but nothing to send: without this the dialog closed with
               no word about the email that never went out -->
          <p v-if="bulkEmailSkipped" class="text-sm text-yellow-700">{{ $t("bulk_alarm_update.email_skipped", { count: bulkEmailSkipped }) }}</p>
        </div>

        <!-- demo feedback: say what "save and send" does before it is clicked -->
        <p v-if="isBulk && bulkResults == null" class="px-2 pb-3 text-xs italic text-gray-500">
          {{ $t("bulk_alarm_update.auto_send_hint") }}
        </p>
      </div>
      <div class="modal-footer" v-if="isBulk">
        <!-- The bulk form starts empty; the alarm central is resolved per
             device at send time (form value, else each device's own), so the
             send button no longer depends on a single alarm_central. -->
        <!-- demo feedback: bulk update is admin only -->
        <div class="footer-container clearfix" v-if="bulkResults == null">
          <button class="btn-red-outline px-3 py-2 float-left" @click="hide('update-alarm-contract')">{{ $t("user_actions.cancel") }}<BaseIcon icon="times-circle" class="ml-1" /></button>
          <!-- SEC-364 A2: Safe-4 gets its data through KUIS, never by email, so when
               every selected device ends up on Safe-4 there is nothing to send -->
          <button v-if="!bulkAllOnSafe4" class="btn-blue-outline px-3 py-2 float-left ml-2" @click="bulkSaveAndSend()">{{ $t("bulk_alarm_update.save_and_send") }}<BaseIcon icon="envelope" class="ml-1" /></button>
          <button class="btn-green px-3 py-2 float-right" @click="bulkSaveOnly()">{{ $t("user_actions.save") }}<BaseIcon icon="save" class="ml-1" /></button>
        </div>
        <div class="footer-container clearfix" v-else>
          <button class="btn-red-outline px-3 py-2 float-left" @click="hide('update-alarm-contract')">{{ $t("user_actions.close") }}<BaseIcon icon="times-circle" class="ml-1" /></button>
          <button v-if="bulkHasFailures" class="btn-green px-3 py-2 float-right" @click="retryFailedBulkSave()">{{ $t("bulk_alarm_update.retry_failed") }}<BaseIcon icon="redo" class="ml-1" /></button>
          <button v-if="bulkEmailFailed" class="btn-blue-outline px-3 py-2 float-right mr-2" @click="retryBulkEmail()">{{ $t("bulk_alarm_update.retry_email") }}<BaseIcon icon="envelope" class="ml-1" /></button>
        </div>
      </div>
      <div class="modal-footer" v-else>
        <div class="footer-container clearfix" v-if="updateInfo && new RegExp('(SRT334)|(SRT330)|(TWIG Beacon)|(Anchor Beacon)|(TELTONIKA)|(4G Box)|(Avigilon Alta Compact Dome 5MP)|(AJAX)').test(updateInfo.type)">
          <button class="btn-red-outline px-3 py-2 float-left" @click="hide('update-alarm-contract')">{{ $t("user_actions.cancel") }}<BaseIcon icon="times-circle" class="ml-1" /></button>
          <button class="btn-green px-3 py-2 float-right" @click="updateAlarmContract()">{{ $t("user_actions.save") }}<BaseIcon icon="save" class="ml-1" /></button>
        </div>
        <div class="footer-container clearfix" v-else>
          <button v-if="user.role == 'superuser' || user.role == 'user' || user.role == 'reseller'" class="btn-red-outline px-3 py-2 float-left" @click="hide('update-alarm-contract')">{{ $t("user_actions.cancel") }}<BaseIcon icon="times-circle" class="ml-1" /></button>
          <!-- Safe-4 (SEC-364 A2): no mail button. The alarm plan reaches Safe-4
               through the KUIS sync, so saving is the only action. For these roles
               "send to alarm central" is also their save button, so it is swapped
               for a plain Save instead of just being hidden. -->
          <button v-if="updateInfo && updateInfo.alarm_central != '-' && !isSafe4Selected && (user.role == 'superuser' || user.role == 'user' || user.role == 'reseller')" class="btn-green px-3 py-2 float-right" @click="saveAndSendPDF()">{{ $t("change_alarm_plan.send_to_alarm_center") }}<BaseIcon icon="save" class="ml-1" /></button>
          <button v-if="updateInfo && isSafe4Selected && (user.role == 'superuser' || user.role == 'user' || user.role == 'reseller')" class="btn-green px-3 py-2 float-right" @click="updateAlarmContract()">{{ $t("user_actions.save") }}<BaseIcon icon="save" class="ml-1" /></button>

          <button v-if="user.role == 'alarm_center'" class="btn-green px-3 py-2 float-left" @click="updateAlarmContract()">{{ $t("user_actions.save") }}<BaseIcon icon="save" class="ml-1" /></button>
          <button v-if="user.role == 'alarm_center'" class="btn-blue-outline px-3 py-2 float-right" @click="downloadPDF()">{{ $t("user_actions.download") }}<BaseIcon icon="file-pdf" class="ml-1" /></button>
          
          <button v-if="updateInfo && updateInfo.alarm_central != '-' && !isSafe4Selected && (user.role == 'admin' || user.role == 'seller')" class="btn-blue-outline px-3 py-2 float-left" @click="saveAndSendPDF()">{{ $t("change_alarm_plan.send_to_alarm_center") }}<BaseIcon icon="envelope" class="ml-1" /></button>
          <button v-if="user.role == 'admin' || user.role == 'seller'" class="btn-green px-3 py-2 float-right" @click="updateAlarmContract()">{{ $t("user_actions.save") }}<BaseIcon icon="save" class="ml-1" /></button>
        </div>
      </div>
    </modal>

    <!-- Save-confirmation (SEC-218 v2): a standard app modal (blue header,
         normal footer) listing the fields and devices that will be updated. -->
    <modal name="bulk-update-confirm" class="modal-inner modal-bulk-update-confirm" transition="pop-out" :width="modalWidth" :focus-trap="true" height="auto" :scrollable="true">
      <span class="close-button" @click="$modal.hide('bulk-update-confirm')"><BaseIcon icon="times-circle" class="text-white"/></span>
      <div class="modal-header text-lg font-serif">
        <span>{{ $t("bulk_alarm_update.confirm_title") }}</span>
      </div>
      <div class="modal-body">
        <p class="text-sm mb-2">{{ $t("bulk_alarm_update.confirm_intro") }}</p>
        <ul class="text-sm font-semibold list-disc list-inside mb-4">
          <li v-for="label in bulkConfirmFieldLabels" :key="label">{{ label }}</li>
        </ul>
        <p class="text-sm mb-1">{{ $t("bulk_alarm_update.confirm_devices") }}</p>
        <ul class="text-sm text-gray-600 list-disc list-inside">
          <li v-for="a in bulkAlarms" :key="a._id">{{ a.unit_name }} - {{ a.phone || a.modelnumber }}</li>
        </ul>
      </div>
      <div class="modal-footer">
        <div class="footer-container clearfix">
          <button class="btn-red px-3 py-2 float-left" @click="$modal.hide('bulk-update-confirm')">{{ $t("user_actions.no") }}<BaseIcon icon="times-circle" class="ml-1" /></button>
          <button class="btn-green px-3 py-2 float-right" @click="onBulkConfirmYes()">{{ $t("user_actions.yes") }}<BaseIcon icon="check" class="ml-1" /></button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
const MODAL_WIDTH = 1200;
// How many alarm documentations one email request carries. The bulk update
// itself is not capped; this only keeps a single request short (SEC-218).
const DOCUMENTS_PER_EMAIL_REQUEST = 20;
// Safe-4 (SEC-364 A3): what goes in a contact person's three time fields. Safe-4
// calls the contacts around the clock, so they hold the whole day - stored the
// way KUIS has it, so SecurCloud mirrors SAFECON (Rasmus, Q6: "store it as KUIS have so it mirrors their system"). KUIS keeps
// 00:00:00-23:59:59 on every contact; this is the same window in the fields'
// own HH:MM-HH:MM format (SEC-320).
const SAFE4_CONTACT_TIME = "00:00-23:59";

// Device types that have no alarm documentation (displays, tags). An SRT430
// display can alarm, so it has an alarm plan like any alarm.
const TYPES_WITHOUT_DOCUMENTATION = ["SRT330", "SRT334", "TWIG Beacon", "Anchor Beacon"];
function hasDocumentation(alarm) {
  return !!alarm && TYPES_WITHOUT_DOCUMENTATION.indexOf(alarm.type) === -1;
}

import DatePicker from "vue2-datepicker";
import "vue2-datepicker/locale/sv";
import "vue2-datepicker/locale/pl";
import "vue2-datepicker/locale/nb";
import "@/utils/datePickerDutch";
import "vue2-datepicker/index.css";
import _ from "lodash";
import siteContext from "@/utils/siteContext";
import { RISK_PROFILE, ALARM_SORT, normalizeRiskProfile, normalizeAlarmSort } from "@/utils/alarmEnums";
import { DEVICE_TYPES } from "@/utils/deviceTypes";
import AlarmProfilePreview from "@/components/alarm_profile_preview.vue";
import { emptyBulkForm, buildSparseBulkPayload, applyBulkDocumentation, mergeContactPersons, patchedContactPositions, buildContactPersonPatch } from "@/utils/alarmDocumentation";
import { deliveryCompanyForSeller } from "@/utils/deliveryCompany";
import { isWhiteLabelCustomer } from "@/utils/brand";
import { isAgreementNumber } from "@/utils/countryOrigin";

export default {
  siteContext,
  props: {
    alarmInfo: {
      required: true,
      default: null,
    },
    alarmCentrals: {
      required: true,
    },
    sellers: {
      required: true,
    },
    customer: {
      required: true,
      default: null,
    },
    // Bulk mode (SEC-218 v2): the devices whose documentation is edited at once.
    // The bulk form starts empty; only the fields support fills in are written
    // to every one of these devices.
    bulkAlarms: {
      type: Array,
      default: null,
    },
    // The customer's businesses (Units): the alarm plan PDF prints a business's
    // customer-specific instruction when it has one
    units: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  components: {
    DatePicker,
    AlarmProfilePreview,
  },
  data() {
    return {
      isLoading: false,
      updateInfo: null,
      // Safe-4: the AlarmCenter _id that is Safe-4 (empty = integration off)
      safe4_center_id: "",
      show_alarm_number: false,
      show_MSL_risk: false,
      show_personal_alarm: false,
      date_picker_lang: this.$i18n.locale,
      date_picker_langKey: 0,
      // bulk mode state (SEC-218 v2)
      bulkForm: emptyBulkForm(), // empty form; only filled fields get written
      bulkDocumentation: null, // the sparse payload actually saved (for retry)
      bulkResults: null, // per-device save outcome: [{alarm_id, unit_name, success, reason}]
      // Operator instructions start collapsed (SEC-364 A1).
      show_operator_instructions: false,
      bulkEmailFailed: false,
      bulkEmailSent: false,
      bulkEmailSkipped: 0, // saved devices with no alarm central to send to
      bulkPendingEmails: null, // [{central, pdfs}] kept for "retry email" without re-saving
      bulkIsNew: false, // true when the alarm central was changed -> "new" email title
      // save-confirmation modal state
      bulkConfirmFieldLabels: [],
      bulkConfirmDocumentation: null,
      bulkConfirmChangedFields: [],
      bulkConfirmSendAfter: false,
    };
  },

  computed: {
    user() {
      return this.$store.state.user;
    },
    // Safe-4 selected in the single-device form?
    isSafe4Selected() {
      return !!this.safe4_center_id && this.updateInfo && this.updateInfo.alarm_central == this.safe4_center_id;
    },
    // an SRT430 that tunnels to the alarm central must have a real one - "-" is none
    srt430AlarmCentralRules() {
      return "required|oneOf:" + (this.alarmCentrals || []).map((a) => a._id).join(",");
    },
    // Safe-4 (SEC-370): a Norwegian site's postcode must be inside the county
    // ranges (utils/safe4Postcodes.js) - the server refuses the save otherwise.
    safe4PostcodeRules() {
      const country = (this.updateInfo && this.updateInfo.country) || (this.$options.siteContext.isNorwegianSite() ? "no" : "se");
      return `required|safe4_postcode:${country}`;
    },
    // SEC-364 A2 / SEC-373: true when every selected device ends up on Safe-4 -
    // the alarm central set in the bulk form, else each device's own (the same
    // rule the email step uses to pick a central). The bulk form's Safe-4 rules
    // follow this, so they also apply to devices that are already on Safe-4.
    bulkAllOnSafe4() {
      if (!this.safe4_center_id || !this.isBulk) return false;
      return this.bulkAlarms.every((alarm) => (this.bulkForm.alarm_central || alarm.alarm_central) == this.safe4_center_id);
    },
    // SEC-370 / SEC-373: true when at least one selected device ends up on
    // Safe-4. A personal alarm cannot be sent to Safe-4 (SEC-364 A16), and the
    // bulk save writes the alarm type to every selected device, so a mixed
    // selection is not offered it either.
    bulkAnyOnSafe4() {
      if (!this.safe4_center_id || !this.isBulk) return false;
      return this.bulkAlarms.some((alarm) => (this.bulkForm.alarm_central || alarm.alarm_central) == this.safe4_center_id);
    },
    // Bulk: the postcode is optional ("no change"), so no `required` - only the
    // country's postcode rule: the Norwegian ranges (SEC-370) or the Swedish
    // five digits (SEC-373). The server checks each device against its own unit
    // country.
    bulkSafe4PostcodeRules() {
      // the country's rule only when every selected device is in that country;
      // a mixed selection is left to the server, which checks each device
      const countries = new Set((this.bulkAlarms || []).map((a) => a.country || "se"));
      return countries.size === 1 ? `safe4_postcode:${[...countries][0]}` : "";
    },
    // Registration state shown under the alarm-central select:
    // 'registered' | 'pending' | '' (not a Safe-4 device)
    safe4Badge() {
      if (!this.isSafe4Selected) return "";
      if (this.updateInfo.safe4_trans_nr && this.updateInfo.safe4_abonnent) return "registered";
      return "pending";
    },
    isBulk() {
      return Array.isArray(this.bulkAlarms) && this.bulkAlarms.length > 0;
    },
    bulkHasFailures() {
      return !!this.bulkResults && this.bulkResults.some((r) => !r.success);
    },
    alarmSortOptions() {
      return [
        { value: ALARM_SORT.GROUP, label: this.$t('change_alarm_plan.group_alarm') },
        { value: ALARM_SORT.PERSONAL, label: this.$t('change_alarm_plan.personal_alarm') },
      ];
    },
    transportOptions() {
      return [
        { value: 'walking', label: this.$t('change_alarm_plan.transport_walking') },
        { value: 'bike', label: this.$t('change_alarm_plan.transport_bike') },
        { value: 'public_transport', label: this.$t('change_alarm_plan.transport_public_transport') },
        { value: 'car', label: this.$t('change_alarm_plan.transport_car') },
      ];
    },
    riskProfileOptions() {
      return [
        { value: RISK_PROFILE.THREAT, label: this.$t('risk_profile.threat_alarm') },
        { value: RISK_PROFILE.EMERGENCY, label: this.$t('risk_profile.emergency_alarm') },
        { value: RISK_PROFILE.ASSAULT, label: this.$t('risk_profile.burglar_alarm') },
      ];
    },
    riskProfileOptionsMSL() {
      // Malmö special-case options are intentionally kept as raw label values
      // (handled separately); only the standard profiles use stable codes.
      return [
        ...this.riskProfileOptions,
        { value: 'Malmö Fast', label: 'Malmö Fast' },
        { value: 'Malmö Rörlig', label: 'Malmö Rörlig' },
        { value: 'Malmö nödsändare', label: 'Malmö nödsändare' },
      ];
    },
    // ---- bulk form option lists (each led by a "don't change" empty option) ----
    bulkNoChange() {
      return { value: "", label: this.$t("bulk_alarm_update.no_change") };
    },
    // SEC-373: guard dispatch can be switched off in bulk too, not only on
    bulkDirectDispatchOptions() {
      return [
        this.bulkNoChange,
        { value: "on", label: this.$t("user_actions.yes") },
        { value: "off", label: this.$t("user_actions.no") },
      ];
    },
    bulkAlarmCentralOptions() {
      return [{ name: this.$t("bulk_alarm_update.no_change"), _id: "" }, ...this.alarmCentrals];
    },
    // Seller for the bulk editor: the same staff/reseller list the single-device
    // form uses, with "no change" first so an untouched select writes nothing.
    bulkSellerOptions() {
      return [{ name: this.$t("bulk_alarm_update.no_change"), _id: "" }, ...this.sellers];
    },
    bulkDeviceStatusOptions() {
      return [
        this.bulkNoChange,
        { value: "ny_bestallning", label: this.$t("device_status.ny_bestallning") },
        { value: "utbytesenhet", label: this.$t("device_status.utbytesenhet") },
        { value: "ersattningsprodukt", label: this.$t("device_status.ersattningsprodukt") },
      ];
    },
    bulkAlarmSortOptions() {
      return [this.bulkNoChange, ...this.alarmSortOptions];
    },
    bulkRiskProfileOptions() {
      return [this.bulkNoChange, ...(this.bulkShowMslRisk ? this.riskProfileOptionsMSL : this.riskProfileOptions)];
    },
    bulkSelectedCentral() {
      if (!this.bulkForm.alarm_central) return null;
      return _.find(this.alarmCentrals, { _id: this.bulkForm.alarm_central });
    },
    bulkShowMslRisk() {
      return !!this.bulkSelectedCentral && /(Malmö Stads Larmcentral)|(Skyresponse Malmö)/.test(this.bulkSelectedCentral.name);
    },
    bulkShowDirectDispatch() {
      // Safe-4 (SEC-316): always available — see showDirectDispatch.
      if (this.bulkAllOnSafe4) return true;
      // NO site only, and only when the chosen central supports guard dispatch
      if (!this.$options.siteContext.isNorwegianSite()) return false;
      return !!this.bulkSelectedCentral && this.bulkSelectedCentral.guard_dispatch === true;
    },
    bulkShowPersonalAlarm() {
      return normalizeAlarmSort(this.bulkForm.selected_alarm_sort) === ALARM_SORT.PERSONAL;
    },
    agreementNumberMask() {
      return this.$options.siteContext.getAgreementNumberMask();
    },
    devices() {
      // Single source of truth: @/utils/deviceTypes (SEC-216)
      return DEVICE_TYPES;
    },
    showDirectDispatch() {
      // Safe-4 (SEC-316): the toggle decides what Safe-4 registers for the
      // device — VEKTERRYKK (guard dispatch) or TELEFONVARSLING (phone only) —
      // so it is always available when the central is Safe-4.
      if (this.isSafe4Selected) return true;
      // Show direct dispatch option only for Norwegian site and if the selected alarm central has guard_dispatch enabled
      if (!this.$options.siteContext.isNorwegianSite()) return false;
      if (!this.updateInfo || !this.updateInfo.alarm_central || this.updateInfo.alarm_central === '-') return false;
      const selectedAlarmCentral = _.find(this.alarmCentrals, { _id: this.updateInfo.alarm_central });
      return selectedAlarmCentral && selectedAlarmCentral.guard_dispatch === true;
    },
    isSosAlarm() {
      if (!this.updateInfo || !this.updateInfo.alarm_central || this.updateInfo.alarm_central === '-') return false;
      const selectedAlarmCentral = _.find(this.alarmCentrals, { _id: this.updateInfo.alarm_central });
      return selectedAlarmCentral && /^SOS Alarm/i.test(selectedAlarmCentral.name);
    },
    // SEC-321: the "Avtalspart" field shows only for Westra + Commuter Security Group AB (CSG)
    isWestraSelected() {
      if (!this.updateInfo || !this.updateInfo.alarm_central || this.updateInfo.alarm_central === '-') return false;
      const selectedAlarmCentral = _.find(this.alarmCentrals, { _id: this.updateInfo.alarm_central });
      return selectedAlarmCentral && /Westra/i.test(selectedAlarmCentral.name);
    },
    isCsgSeller() {
      const selectedSeller = _.find(this.sellers, { _id: this.updateInfo && this.updateInfo.seller });
      return selectedSeller && selectedSeller.name === 'Commuter Security Group AB (CSG)';
    },
    showContractParty() {
      return this.isWestraSelected && this.isCsgSeller;
    },
    // SEC-337: the IMSI field belongs to one alarm centre only. The name is a
    // fixed value, matched exactly - a looser match would also catch the
    // separate "Securitas AB" centre, which must not show the field.
    showImsiNumber() {
      if (!this.updateInfo || !this.updateInfo.alarm_central || this.updateInfo.alarm_central === '-') return false;
      const selectedAlarmCentral = _.find(this.alarmCentrals, { _id: this.updateInfo.alarm_central });
      return !!selectedAlarmCentral && selectedAlarmCentral.name === 'Securitas Sverige AB';
    },
    // SEC-321: proxy the dropdown so the None option has a real value ('none')
    // instead of '' (BaseSelect already renders a value="" placeholder, which
    // would otherwise shadow it). The stored contract_party stays "" for None.
    contractPartyChoice: {
      get() {
        return (this.updateInfo && this.updateInfo.contract_party) || 'none';
      },
      set(v) {
        if (this.updateInfo) this.updateInfo.contract_party = v === 'none' ? '' : v;
      },
    },
    isNorwegianSite() {
      return this.$options.siteContext.isNorwegianSite();
    },
    datePickerLocale() {
      // Map 'no' to 'nb' for date picker (Norwegian Bokmål)
      return this.date_picker_lang === 'no' ? 'nb' : this.date_picker_lang;
    },
    // Check risk profile against all possible translations
    isThreatAlarm() {
      if (!this.updateInfo) return false;
      return normalizeRiskProfile(this.updateInfo.risk_profile) === RISK_PROFILE.THREAT;
    },
    isEmergencyAlarm() {
      if (!this.updateInfo) return false;
      return normalizeRiskProfile(this.updateInfo.risk_profile) === RISK_PROFILE.EMERGENCY;
    },
    isBurglarAlarm() {
      if (!this.updateInfo) return false;
      return normalizeRiskProfile(this.updateInfo.risk_profile) === RISK_PROFILE.ASSAULT;
    },
    // SEC-334: customer of a white label reseller, see utils/brand.isWhiteLabelCustomer
    whiteLabelCustomer() {
      return isWhiteLabelCustomer(this.user);
    },
    sim_operators() {
      return ["Telia", "Telia DCP", "Telenor", "Tele2", this.$t('change_alarm_plan.customer_sim_card')];
    },
  },

  watch: {
    // Safe-4 (SEC-364 A3/A5): the moment the alarm central becomes Safe-4 the
    // contact time fields and the personal password stop applying, so they are
    // set to the Safe-4 defaults — including when the alarm came from another
    // alarm central and those fields still hold its values.
    isSafe4Selected(isSafe4) {
      if (isSafe4) this.applySafe4FieldDefaults();
    },
    // SEC-373: a Safe-4 device opened right after another one - the central
    // stays Safe-4, so the watcher above does not fire - gets the defaults too,
    // so its locked time fields show the whole day and not what it had stored
    "updateInfo.contact_persons"() {
      if (this.isSafe4Selected) this.applySafe4FieldDefaults();
    },
    // SEC-364 A5 / SEC-373: once every selected device ends up on Safe-4 the
    // personal password field is hidden, so what was typed there before is
    // emptied too - as the single-device forms do when Safe-4 is chosen - and
    // the save neither sends it nor lists it in the confirmation
    bulkAllOnSafe4(allOnSafe4) {
      if (allOnSafe4) this.$set(this.bulkForm, "personal_password", "");
    },
    // SEC-370 / SEC-373: once a Safe-4 device comes into the bulk update (e.g.
    // the bulk central set to Safe-4 after the alarm type was picked), a picked
    // personal alarm goes back to "no change" and the personal alarm fields are
    // emptied, so the save does not write them to the Safe-4 devices.
    bulkAnyOnSafe4(anyOnSafe4) {
      if (!anyOnSafe4) return;
      if (this.bulkShowPersonalAlarm) this.$set(this.bulkForm, "selected_alarm_sort", "");
      this.$set(this.bulkForm, "personal_alarm", emptyBulkForm().personal_alarm);
    },
    alarmInfo(newVal) {
      this.show_operator_instructions = false; // every device opens collapsed (SEC-373)
      // eslint-disable-line
      this.updateInfo = _.cloneDeep(newVal);
      // Normalize legacy translated-label values to canonical codes so the
      // dropdowns select correctly regardless of which language they were saved in.
      this.updateInfo.risk_profile = normalizeRiskProfile(this.updateInfo.risk_profile);
      this.updateInfo.selected_alarm_sort = normalizeAlarmSort(this.updateInfo.selected_alarm_sort);
      // Ensure reference_number is reactive for alarms saved before this field existed.
      this.$set(this.updateInfo, "reference_number", this.updateInfo.reference_number || "");
      // SEC-321: keep Avtalspart reactive and preserve the saved value (empty = None).
      this.$set(this.updateInfo, "contract_party", this.updateInfo.contract_party || "");
      // SEC-337: keep the IMSI number reactive, so it can be typed into on devices
      // saved before the field existed, and keeps its value on the ones that have it.
      this.$set(this.updateInfo, "imsi_number", this.updateInfo.imsi_number || "");
      this.updateInfo.ava_flex = this.updateInfo.ava_flex || {};
      // Safe-4 (req 8): "Based on address" is split into street / zip / city.
      // Normalize old devices where it was a single free-text value.
      const fa = this.updateInfo.from_address || {};
      this.$set(this.updateInfo, "from_address", { street: fa.street || "", zip: fa.zip || "", city: fa.city || "" });
      // Guard nested objects the template dereferences unconditionally so an
      // alarm missing any of them doesn't crash the render.
      this.updateInfo.delivery_company = this.updateInfo.delivery_company || {};
      this.syncDeliveryCompany();
      this.updateInfo.external_contractor = this.updateInfo.external_contractor || {};
      this.updateInfo.personal_alarm = this.updateInfo.personal_alarm || {};
      // Ensure personal-alarm sub-fields are reactive for alarms saved before they existed.
      ["reporting", "risk_profile", "name", "home_address", "work_address", "transport", "registration_number", "model", "color"].forEach((k) => {
        if (this.updateInfo.personal_alarm[k] === undefined) this.$set(this.updateInfo.personal_alarm, k, "");
      });
      // Always offer the three contact-person rows the create-device page and
      // automatic device creation store. A device whose documentation ended up
      // with fewer rows (an older device, or a bulk update that filled in only
      // contact person 1) used to render only those rows, leaving support with
      // no field to enter contact person 2 or 3 into.
      this.updateInfo.contact_persons = mergeContactPersons(this.updateInfo.contact_persons, []);
      if (!this.updateInfo.device_status) this.updateInfo.device_status = 'ny_bestallning';
      if (!this.updateInfo.securDesktop) {
        this.$set(this.updateInfo, "securDesktop", { contactPersons: [{ name: "", phone: "" }] });
      } else if (!Array.isArray(this.updateInfo.securDesktop.contactPersons)) {
        this.$set(this.updateInfo.securDesktop, "contactPersons", [{ name: "", phone: "" }]);
      } else if (this.updateInfo.securDesktop.contactPersons.length === 0) {
        this.$set(this.updateInfo.securDesktop, "contactPersons", [{ name: "", phone: "" }]);
      }
      this.check_personal_alarm();
      this.check_alarm_center_specifics();
    },
    // Bulk mode (SEC-218 v2): the bulk form starts completely empty - no
    // reference device is used. Opening the editor resets the form and the
    // per-session state so nothing leaks from a previous bulk session.
    // Leaving bulk mode (the parent clears bulkAlarms) resets it too:
    // otherwise the finished bulk result panel is still shown the next time a
    // single device's documentation is opened, hiding the form (demo feedback).
    bulkAlarms() {
      this.bulkForm = emptyBulkForm();
      this.resetBulkState();
    },
    alarmCentrals() {
      // eslint-disable-line
      this.check_alarm_center_specifics();
    },
    // The "Företag" fields follow the seller (see syncDeliveryCompany)
    "updateInfo.seller"() {
      this.syncDeliveryCompany();
    },
    sellers() {
      this.syncDeliveryCompany();
    },
    "$i18n.locale"(newLocale) {
      this.date_picker_lang = newLocale;
      this.date_picker_langKey++;
    },
  },

  methods: {
    // the business an alarm belongs to, for the PDF's instruction section
    unitForAlarm(alarm) {
      if (!alarm || !alarm.parent_id) return null;
      return _.find(this.units, { unit_id: alarm.parent_id }) || null;
    },

    /**
     * Put the Safe-4 defaults into the fields Safe-4 does not use (SEC-364).
     *
     * 1. Contact persons are called around the clock, so all three time fields
     *    hold the whole day — whatever a previous alarm central left there is replaced.
     * 2. KUIS has no personal password, so that field is emptied; the template
     *    hides it while Safe-4 is selected.
     */
    applySafe4FieldDefaults() {
      if (!this.updateInfo) return;

      const contacts = this.updateInfo.contact_persons || [];
      contacts.forEach((contact) => {
        this.$set(contact, "weekday", SAFE4_CONTACT_TIME);
        this.$set(contact, "saturday", SAFE4_CONTACT_TIME);
        this.$set(contact, "sunday", SAFE4_CONTACT_TIME);
      });

      this.$set(this.updateInfo, "personal_password", "");

      // A16: a personal alarm cannot be sent to Safe-4, so it becomes a group alarm
      if (normalizeAlarmSort(this.updateInfo.selected_alarm_sort) === ALARM_SORT.PERSONAL) {
        this.$set(this.updateInfo, "selected_alarm_sort", ALARM_SORT.GROUP);
        this.show_personal_alarm = false;
      }
    },
    /**
     * Upper-case the fields KUIS keeps in upper case (SEC-364): the device
     * password, every contact person's name and function, and the street of
     * the "Based on address". Run just before saving, so typing stays normal.
     */
    applySafe4UpperCase() {
      if (!this.updateInfo) return;

      const upper = (value) => String(value == null ? "" : value).trim().toUpperCase();

      this.updateInfo.alarm_password = upper(this.updateInfo.alarm_password);

      (this.updateInfo.contact_persons || []).forEach((contact) => {
        this.$set(contact, "name", upper(contact.name));
        this.$set(contact, "function", upper(contact.function));
      });

      const address = this.updateInfo.from_address || {};
      this.$set(address, "street", upper(address.street));
    },
    // The "Företag" fields: the reseller's name, phone and e-mail when a reseller sold the
    // device, Securdia for the device's country otherwise. A seller missing from the list
    // (not loaded yet) leaves the stored values alone.
    syncDeliveryCompany() {
      if (!this.updateInfo) return;
      // SEC-334: for a white label customer the brand delivers the device: the contact from the
      // reseller's branding, the same one the alarm plan PDF and the sidebar show
      if (this.whiteLabelCustomer) {
        const contact = this.$brand.contact;
        this.updateInfo.delivery_company = { name: contact.company_name, phone: contact.phone, email: contact.email };
        return;
      }
      const country = this.updateInfo.country || (this.$options.siteContext.isNorwegianSite() ? "no" : "se");
      const company = deliveryCompanyForSeller(this.sellers, this.updateInfo.seller, country);
      if (company) this.updateInfo.delivery_company = company;
    },
    updateAlarmContract() {
      return this.$refs.formUpdateAlarmContract.validate().then((success) => {
        if (!success) {
          this.handleError(null, this.$t("change_alarm_plan.fields_are_empty"));
          return;
        }

        // Safe-4 (SEC-364): KUIS stores these fields in upper case, so they are
        // converted before the alarm is saved as well as before it is sent.
        if (this.isSafe4Selected) this.applySafe4UpperCase();

        let validContacts = (this.updateInfo.securDesktop && this.updateInfo.securDesktop.contactPersons) ? this.updateInfo.securDesktop.contactPersons.filter(contact => {
          return contact.name && contact.phone && (contact.name.trim() !== '' || contact.phone.trim() !== '');
        }): [];

        // use serial number for imei, incase imei is optional
        let imeiNumber = this.updateInfo.imei_number;
        if(/^AJAX/.test(this.updateInfo.type)) imeiNumber = this.updateInfo.modelnumber;
        
        let data = {
          type: this.updateInfo.type,
          imei_number: imeiNumber,
          phone: this.updateInfo.phone,
          modelnumber: this.updateInfo.modelnumber,
          operator: this.updateInfo.operator,
          alarm_central: this.updateInfo.alarm_central,
          alarm_number: this.updateInfo.alarm_number,
          imsi_number: (this.updateInfo.imsi_number || "").trim(), // SEC-337
          man_down: this.updateInfo.man_down,
          direct_dispatch: this.updateInfo.direct_dispatch,
          agreement_number: this.updateInfo.agreement_number,
          agreement_length: this.updateInfo.agreement_length,
          seller: this.updateInfo.seller,
          contract_party: this.updateInfo.contract_party || "", // SEC-321
          delivery_company: this.updateInfo.delivery_company,
          selected_alarm_sort: this.updateInfo.selected_alarm_sort,
          personal_alarm: this.updateInfo.personal_alarm,
          risk_profile: this.updateInfo.risk_profile,
          reference_number: this.updateInfo.reference_number,
          user: this.updateInfo.user,
          unit_name: this.updateInfo.unit_name,
          from_address: this.updateInfo.from_address,
          alarm_unit_name: this.updateInfo.alarm_unit_name,
          business_description: this.updateInfo.business_description,
          alarm_password: this.updateInfo.alarm_password,
          personal_password: this.updateInfo.personal_password,
          external_contractor: this.updateInfo.external_contractor,
          contact_persons: this.updateInfo.contact_persons,
          comment: this.updateInfo.comment,
          contract_start: this.updateInfo.contract_start,
          ava_flex : {
            tag_serial_number: this.updateInfo.ava_flex.tag_serial_number,
            webhook: this.updateInfo.ava_flex.webhook,
            camera_token: this.updateInfo.ava_flex.camera_token,
          },
          inventory_number: this.updateInfo.inventory_number,
          securDesktop: {
            alarmMessage: this.updateInfo.securDesktop.alarmMessage,
            endMessage: this.updateInfo.securDesktop.endMessage,
            contactPersons: validContacts,
          },
          device_status: this.updateInfo.device_status || 'ny_bestallning',

        };


        // Safe-4 registration runs during this request — show progress (req 6).
        this.isLoading = true;
        return this.axios
          .put(`${process.env.VUE_APP_SERVER_URL}/alarms/${this.updateInfo._id}`, data)
          .then((response) => {
            //eslint-disable-line
            this.isLoading = false;
            this.$emit("updateAlarmContract");

            // Safe-4 result (req 6): saved but NOT synced -> the user must
            // acknowledge and stays on the action document.
            const s4 = response.data && response.data.safe4;
            if (s4 && s4.attempted && !s4.ok) {
              const message = s4.buffered
                ? this.$t("safe4_form.sync_buffered")
                : this.$t("safe4_form.sync_failed", { code: s4.errorCode || s4.error || "?" });
              this.handleError(null, message);
              return Promise.resolve();
            }
            if (s4 && s4.attempted && s4.ok) this.handleSuccess(s4.removed ? this.$t("safe4_form.removed") : this.$t("safe4_form.synced"));

            this.$modal.hide("update-alarm-contract");
            this.$modal.hide("update-alarm");
            return Promise.resolve();
          })
          .catch((error) => {
            this.isLoading = false;
            // duplicate IMEI / serial / phone / inventory number (SEC-216)
            // Safe-4 refused the data: nothing was saved (SEC-364 B1)
            if (!this.handleSafe4Rejection(error, this.$refs.formUpdateAlarmContract) && !this.handleIdentifierConflict(error)) {
              this.handleError(error);
            }
            return Promise.reject(error);
          });
      });
    },

    saveAndSendPDF() {
      this.$refs.formUpdateAlarmContract.validate().then(async (success) => {
        if (!success) {
          this.handleError(null, this.$t("change_alarm_plan.fields_are_empty"));
          return;
        }

        this.isLoading = true;
        // Snapshot the original alarm data BEFORE saving, so we can show old → new in the PDF
        const oldAlarmSnapshot = _.cloneDeep(this.alarmInfo);
        const alarmSnapshot = _.cloneDeep(this.updateInfo);
        try {
          await this.updateAlarmContract();
          await this.sendPDF(oldAlarmSnapshot, alarmSnapshot);
        } catch (error) {
          this.isLoading = false;
        }
      });
    },

    async sendPDF(oldAlarmSnapshot, alarmSnapshot) {
      let alarm = alarmSnapshot || this.updateInfo;

      // send only for alarms
      if (!hasDocumentation(alarm)) {
        return;
      }

      this.isLoading = true;

      try {
        // Validate alarm_central exists
        if (!alarm.alarm_central || alarm.alarm_central === '-') {
          throw new Error("No alarm central selected");
        }

        let customerName = "";
        if (this.customer) customerName = this.customer.name;

        let doc = await this.generateAlarmPlanPDF({
          alarm: alarm,
          customerName: customerName, 
          sellers: this.sellers, 
          oldAlarm: oldAlarmSnapshot || this.alarmInfo, 
          highlight: true, 
          showAlarmNumber: this.show_alarm_number,
          showImsiNumber: this.showImsiNumber, // SEC-337
          showDirectDispatch: this.showDirectDispatch,
          unit: this.unitForAlarm(alarm),
          customer: this.customer,
        });
        var base = doc.output("blob");

        let headers = {
          "X-Access-Token": this.$store.state.token,
          "Content-Type": "multipart/form-data",
        };

        // If the alarm central was changed, this documentation reaches the new
        // central for the first time, so it is sent as "new" (email title) even
        // though the device already existed. Otherwise it stays an update.
        const oldAlarm = oldAlarmSnapshot || this.alarmInfo;
        const centralChanged = !!oldAlarm && oldAlarm.alarm_central !== alarm.alarm_central;

        var formData = new FormData();
        var pdfFile = base;
        formData.append("pdf", pdfFile);
        formData.append("alarm_id", alarm._id);
        formData.append("is_new", centralChanged ? "true" : "false");

        const response = await this.axios.post(
          `${process.env.VUE_APP_SERVER_URL}/alarm-center/${alarm.alarm_central}/email`, 
          formData, 
          headers
        );

        if (response.data.error) {
          this.handleError(null, this.$t("change_alarm_plan.email_send_error"));
        } else if (response.data.success) {
          this.handleSuccess(this.$t("change_alarm_plan.sent_to_alarm_center"));
        }
        
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        this.handleError(error, this.$t("change_alarm_plan.email_send_error"));
        throw error;
      }
    },

    async downloadPDF() {
      try {
        // download only for alarms
        if (!hasDocumentation(this.updateInfo)) return;

        let customerName = "";
        if (this.customer) customerName = this.customer.name;

        let doc = await this.generateAlarmPlanPDF({ alarm: this.updateInfo, customerName: customerName, sellers: this.sellers, oldAlarm: this.alarmInfo, highlight: false, showAlarmNumber: this.show_alarm_number, showImsiNumber: this.showImsiNumber, showDirectDispatch: this.showDirectDispatch, unit: this.unitForAlarm(this.updateInfo), customer: this.customer });
        doc.save(`${this.updateInfo.alarm_unit_name}-${this.updateInfo.unit_name}-${this.updateInfo.phone}.pdf`);
      } catch (error) {
        this.handleError(error);
      }
    },
    
    // SEC-321: when the alarm central changes, run the existing specifics check
    // and then re-evaluate the Avtalspart field. Wrapped so the existing method
    // stays untouched.
    onAlarmCentralChange() {
      this.check_alarm_center_specifics();
      this.check_contract_party();
      this.check_imsi_number();
    },
    // SEC-337: clear the IMSI number when the user picks a different alarm
    // centre. Bound to the dropdown's @change (user actions only), so opening a
    // saved Securitas device never wipes the value it already has.
    check_imsi_number() {
      if (!this.updateInfo) return;
      if (!this.showImsiNumber) this.updateInfo.imsi_number = '';
    },
    // SEC-321: default Avtalspart to CSG when the user makes the field visible
    // (Westra + CSG seller), and clear it whenever that combination is broken.
    // Bound to @change (user actions only) so loading a saved alarm never
    // overrides its stored value.
    check_contract_party() {
      if (!this.updateInfo) return;
      if (this.showContractParty) {
        if (!this.updateInfo.contract_party) this.updateInfo.contract_party = 'Commuter Security Group AB (CSG)';
      } else {
        this.updateInfo.contract_party = '';
      }
    },
    check_alarm_center_specifics() {
      if (this.updateInfo) {
        let a = _.find(this.alarmCentrals, { _id: this.updateInfo.alarm_central });

        //Show alarm number field
        if (a && /(Stanley Security)|(Securitas AB)|(Rapid Larmcentral)|(Cubsec Larmcentral)|(Security Assistance Syd AB)|(Addici Security AB)|(Avarn HTTP Test)|(Avarn Security AS)|(NO - alarm central)/.test(a.name)) {
          this.show_alarm_number = true;
          // For Norwegian site, auto-generate alarm number from serial number
          this.generateAlarmNumberForNorway();
        }
          else {
            this.updateInfo.alarm_number = "";
            this.show_alarm_number = false;
          }
          // Show alarm center specific risk profiles
          if (a && /(Malmö Stads Larmcentral)|(Skyresponse Malmö)/.test(a.name)) {
            this.show_MSL_risk = true;
          } else {
            this.show_MSL_risk = false;
          }
      }
    },

    generateAlarmNumberForNorway() {
      // Only for Norwegian site and when alarm number field is shown
      // Only auto-generate if alarm_number is empty (preserve existing saved values from database)
      if (this.$options.siteContext.isNorwegianSite() && this.show_alarm_number && this.updateInfo && this.updateInfo.modelnumber && !this.updateInfo.alarm_number) {
        // Get last 6 digits of serial number (modelnumber) and prepend with 8
        const last6Digits = this.updateInfo.modelnumber.slice(-6);
        this.updateInfo.alarm_number = '8' + last6Digits;
      }
    },

    regenerateAlarmNumberFromSerial() {
      // Force regenerate alarm number from serial number (modelnumber)
      if (this.updateInfo && this.updateInfo.modelnumber) {
        const last6Digits = this.updateInfo.modelnumber.slice(-6);
        this.updateInfo.alarm_number = '8' + last6Digits;
      }
    },

    check_personal_alarm() {
      if (this.updateInfo && normalizeAlarmSort(this.updateInfo.selected_alarm_sort) === ALARM_SORT.PERSONAL) this.show_personal_alarm = true;
      else this.show_personal_alarm = false;
    },

    switchAgreementType() {
      if (this.updateInfo.cash_agreement) {
        this.updateInfo.agreement_number = "Kontant";
        this.updateInfo.agreement_length = "-";
      } else if (!this.updateInfo.cash_agreement && isAgreementNumber(this.alarmInfo.agreement_number)) {
        this.updateInfo.agreement_number = this.alarmInfo.agreement_number;
        this.updateInfo.agreement_length = this.alarmInfo.agreement_length;
      } else {
        this.updateInfo.agreement_number = "";
        this.updateInfo.agreement_length = "";
      }
    },

    hide(id) {
      if (this.$refs.formUpdateAlarmContract) this.$refs.formUpdateAlarmContract.reset();
      // closing after a bulk save (also a partial one) must refresh the list -
      // some devices were already updated
      if (this.isBulk && this.bulkResults) {
        this.closeBulk();
        return;
      }
      this.$modal.hide(id);
    },

    // ---- bulk mode (SEC-218 v2 - empty-field sparse update) ---------------

    resetBulkState() {
      this.bulkResults = null;
      this.bulkEmailFailed = false;
      this.bulkEmailSent = false;
      this.bulkEmailSkipped = 0;
      this.bulkPendingEmails = null;
      this.bulkDocumentation = null;
      this.bulkIsNew = false;
      this.bulkConfirmFieldLabels = [];
      this.bulkConfirmDocumentation = null;
      this.bulkConfirmChangedFields = [];
      this.bulkConfirmSendAfter = false;
      // the save-confirmation popup lives outside the editor modal, so close it
      // here as well - a leftover open dialog cannot be dismissed afterwards
      this.$modal.hide("bulk-update-confirm");
    },

    closeBulk() {
      // the parent clears bulkAlarms (which resets the bulk state) and reloads
      this.$emit("updateAlarmContract");
      this.$modal.hide("update-alarm-contract");
    },

    // human-readable label for a changed field key, for the confirmation popup
    bulkFieldLabel(key) {
      const labels = {
        alarm_central: this.$t("change_alarm_plan.alarm_center"),
        seller: this.$t("change_alarm_plan.seller"),
        alarm_unit_name: this.$t("change_alarm_plan.business_name"),
        from_address: this.$t("change_alarm_plan.based_on_address"),
        business_description: this.$t("change_alarm_plan.work_assignment"),
        selected_alarm_sort: this.$t("change_alarm_plan.alarm_type"),
        risk_profile: this.$t("change_alarm_plan.risk_profile"),
        user: this.$t("change_alarm_plan.number_of_users"),
        reference_number: this.$t("change_alarm_plan.reference_number"),
        alarm_password: this.$t("change_alarm_plan.password"),
        personal_password: this.$t("change_alarm_plan.personal_password"),
        comment: this.$t("change_alarm_plan.comment"),
        man_down: this.$t("change_alarm_plan.man_down"),
        direct_dispatch: this.$t("change_alarm_plan.direct_dispatch"),
        agreement_number: this.$t("change_alarm_plan.agreement_number"),
        device_status: this.$t("device_status.label"),
        contact_persons: this.$t("change_alarm_plan.contact_person"),
        personal_alarm: this.$t("change_alarm_plan.personal_alarm"),
        external_contractor: this.$t("change_alarm_plan.external_agent"),
      };
      return labels[key] || key;
    },

    bulkSaveOnly() {
      this.confirmBulkSave(false);
    },
    // SEC-364 / SEC-373: in bulk, a number may not repeat one that stays on any
    // of the selected devices either, not only one in the other bulk rows. Each
    // device's contacts are built the way the bulk save leaves them: a filled
    // row replaces that slot, an empty row keeps the device's own contact.
    bulkSafe4PhoneRules(index) {
      const rows = this.bulkForm.contact_persons || [];
      const patch = buildContactPersonPatch(rows) || [];
      const others = [];
      (this.bulkAlarms || []).forEach((alarm) => {
        mergeContactPersons(alarm.contact_persons, patch).forEach((contact, i) => {
          if (i !== index) others.push(contact);
        });
      });
      // safe4PhoneRules compares the number at position 0 with every other one in the list
      return this.safe4PhoneRules([rows[index], ...others], 0, this.bulkAllOnSafe4, false);
    },

    bulkSaveAndSend() {
      this.confirmBulkSave(true);
    },

    // Save-confirmation popup: lists the fields that will change and the
    // devices, and requires an explicit Yes before anything is written. Uses a
    // standard app modal (blue header) rather than the generic dialog.
    async confirmBulkSave(sendAfter) {
      // SEC-373: the bulk form's field rules are checked before anything is sent,
      // as on a single save - they used to be shown but never enforced
      const valid = await this.$refs.formUpdateAlarmContract.validate();
      if (!valid) {
        this.handleError(null, this.$t("change_alarm_plan.fields_are_empty"));
        return;
      }

      const { documentation, changedFields } = buildSparseBulkPayload(this.bulkForm);

      // only the fields the user actually filled are written; if none, stop
      if (changedFields.length === 0) {
        this.handleError(null, this.$t("bulk_alarm_update.nothing_to_update"));
        return;
      }

      this.bulkConfirmDocumentation = documentation;
      this.bulkConfirmChangedFields = changedFields;
      this.bulkConfirmSendAfter = sendAfter;
      // Contact persons are written slot by slot, so the popup names the slots:
      // "the other contact persons stay as they are" is the whole point of the
      // positional patch and support has to be able to see it before saving.
      this.bulkConfirmFieldLabels = changedFields.map((k) => {
        if (k !== "contact_persons") return this.bulkFieldLabel(k);
        const positions = patchedContactPositions(documentation.contact_persons);
        return `${this.bulkFieldLabel(k)} (${positions.join(", ")})`;
      });
      this.$modal.show("bulk-update-confirm");
    },

    onBulkConfirmYes() {
      this.$modal.hide("bulk-update-confirm");
      this.runBulkSave(this.bulkConfirmDocumentation, this.bulkConfirmChangedFields, this.bulkConfirmSendAfter);
    },

    // PUT the sparse documentation to the given devices; only the keys present
    // in `documentation` are written - every other field is left untouched.
    async putBulkDocumentation(alarmIds, documentation) {
      this.isLoading = true;
      try {
        const response = await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/alarms/bulk-documentation`, {
          alarm_ids: alarmIds,
          documentation,
        });
        this.isLoading = false;
        return response.data;
      } catch (error) {
        this.isLoading = false;
        this.handleError(error);
        return null;
      }
    },

    async runBulkSave(documentation, changedFields, sendAfter) {
      // snapshot BEFORE saving so the PDFs can highlight old -> new
      const oldAlarms = _.cloneDeep(this.bulkAlarms);
      this.bulkDocumentation = documentation; // stashed for "retry failed"
      // changing the alarm central makes the documentation "new" to that central
      this.bulkIsNew = changedFields.indexOf("alarm_central") > -1;

      const result = await this.putBulkDocumentation(this.bulkAlarms.map((a) => a._id), documentation);
      if (!result) return;

      if (!sendAfter) {
        if (result.failedCount === 0) {
          this.handleSuccess(this.$t("bulk_alarm_update.saved", { count: result.updatedCount }));
          this.closeBulk();
        } else {
          this.bulkResults = result.results; // show per-device outcome + retry
        }
        return;
      }

      // Save and send: email the updated devices' PDFs, grouped by alarm central
      this.bulkResults = result.results;
      const updatedIds = result.results.filter((r) => r.success).map((r) => r.alarm_id);
      if (updatedIds.length === 0) return;

      await this.prepareBulkEmails(oldAlarms, updatedIds, documentation);

      // devices without an alarm central (or without a documentation PDF) are
      // saved but cannot be emailed - say so instead of closing silently
      const sendable = (this.bulkPendingEmails || []).reduce((count, group) => count + group.pdfs.length, 0);
      // Safe-4 devices are left out on purpose (SEC-364 A2), so they must not be
      // counted as devices that "have no alarm central"
      const onSafe4 = oldAlarms.filter((alarm) => updatedIds.indexOf(alarm._id) > -1
        && this.safe4_center_id && (documentation.alarm_central || alarm.alarm_central) == this.safe4_center_id).length;
      this.bulkEmailSkipped = Math.max(0, updatedIds.length - sendable - onSafe4);
      if (sendable === 0) return;

      await this.postBulkEmails();

      if (result.failedCount === 0 && !this.bulkEmailFailed && this.bulkEmailSkipped === 0) this.closeBulk();
    },

    // Group the documents by alarm central: devices may no longer share a central
    // (there is no comparison in v2), so the resolved central is the one just
    // set in the form, else each device's own. PDFs are kept for "retry email".
    async prepareBulkEmails(oldAlarms, updatedIds, documentation) {
      this.isLoading = true;
      try {
        const customerName = this.customer ? this.customer.name : "";
        const groups = {}; // central _id -> [{alarm_id, blob}]

        for (const oldAlarm of oldAlarms) {
          if (updatedIds.indexOf(oldAlarm._id) === -1) continue;
          // no documentation PDF exists for these types (same guard as sendPDF)
          if (!hasDocumentation({ ...oldAlarm, ...documentation })) continue;

          const central = documentation.alarm_central || oldAlarm.alarm_central;
          if (!central || central === "-") continue; // no central -> cannot send
          // SEC-364 A2: Safe-4 gets its data through KUIS, never by email, even
          // when the selection mixes Safe-4 devices with other alarm centrals
          if (this.safe4_center_id && central == this.safe4_center_id) continue;

          // contact_persons is a positional patch, not a replacement, so the
          // PDF has to show the same per-slot merge the server writes -
          // spreading the patch straight in would print the untouched slots as
          // empty (and its nulls would crash generation)
          const newAlarm = applyBulkDocumentation(oldAlarm, documentation);
          // the PDF dereferences these nested objects unguarded, so an alarm
          // missing any of them would stop generation (the contact list is read
          // row-by-row and guarded inside generateAlarmPlanPDF itself)
          const safeOldAlarm = {
            ...oldAlarm,
            from_address: oldAlarm.from_address || {},
            personal_alarm: oldAlarm.personal_alarm || {},
            external_contractor: oldAlarm.external_contractor || {},
          };
          const doc = await this.generateAlarmPlanPDF({
            alarm: newAlarm,
            customerName,
            sellers: this.sellers,
            oldAlarm: safeOldAlarm,
            highlight: true,
            showDirectDispatch: this.bulkShowDirectDispatch,
            unit: this.unitForAlarm(oldAlarm),
            customer: this.customer,
          });
          (groups[central] = groups[central] || []).push({ alarm_id: oldAlarm._id, blob: doc.output("blob") });
        }

        this.bulkPendingEmails = Object.keys(groups).map((central) => ({ central, pdfs: groups[central] }));
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        this.bulkEmailFailed = true;
        this.handleError(error, this.$t("change_alarm_plan.email_send_error"));
      }
    },

    // The PDFs still waiting to be emailed, grouped by alarm central. The
    // server sends one email per document (demo feedback), so a request is just
    // how the documents get there. Devices are already saved - an email failure
    // is shown with retry and never rolls back.
    //
    // There is no limit on how many devices a bulk update may cover, so the
    // documents are sent in batches: one huge request would sit open for
    // minutes and risk timing out halfway. Documents that reached the server
    // are dropped from the pending list, so "retry email" only re-sends what
    // failed - the alarm central never receives the same documentation twice.
    async postBulkEmails() {
      if (!this.bulkPendingEmails || this.bulkPendingEmails.length === 0) return;

      this.isLoading = true;
      this.bulkEmailFailed = false;
      let anyFailed = false;
      let lastError = null;

      for (const group of this.bulkPendingEmails) {
        const failed = [];

        try {
          while (group.pdfs.length > 0) {
            const batch = group.pdfs.slice(0, DOCUMENTS_PER_EMAIL_REQUEST);

            const formData = new FormData();
            const ids = [];
            batch.forEach((p) => {
              formData.append("pdfs", p.blob, `${p.alarm_id}.pdf`);
              ids.push(p.alarm_id);
            });
            formData.append("alarm_ids", JSON.stringify(ids));
            formData.append("is_new", this.bulkIsNew ? "true" : "false");

            const response = await this.axios.post(
              `${process.env.VUE_APP_SERVER_URL}/alarm-center/${group.central}/email/bulk`,
              formData,
              { headers: { "X-Access-Token": this.$store.state.token, "Content-Type": "multipart/form-data" } }
            );

            const data = response.data || {};
            // older responses name no devices - then the whole batch is retried
            const failedIds = Array.isArray(data.failed_alarm_ids) ? data.failed_alarm_ids : (data.error ? ids : []);

            // this batch has been through the server, whatever the outcome
            group.pdfs = group.pdfs.slice(batch.length);
            failed.push(...batch.filter((p) => failedIds.indexOf(p.alarm_id) > -1));
          }
        } catch (error) {
          // the request itself failed - the batches not yet sent stay pending
          lastError = error;
        }

        group.pdfs = [...failed, ...group.pdfs];
        if (group.pdfs.length > 0) anyFailed = true;
      }

      this.isLoading = false;
      if (anyFailed) {
        this.bulkEmailFailed = true;
        this.handleError(lastError, this.$t("change_alarm_plan.email_send_error"));
      } else {
        this.bulkEmailSent = true;
        this.handleSuccess(this.$t("change_alarm_plan.sent_to_alarm_center"));
      }
    },

    // retry the save for the devices that failed, with the same sparse edit
    async retryFailedBulkSave() {
      const failedIds = this.bulkResults.filter((r) => !r.success).map((r) => r.alarm_id);
      if (failedIds.length === 0 || !this.bulkDocumentation) return;

      const result = await this.putBulkDocumentation(failedIds, this.bulkDocumentation);
      if (!result) return;

      const retried = _.keyBy(result.results, "alarm_id");
      this.bulkResults = this.bulkResults.map((r) => (retried[r.alarm_id] ? retried[r.alarm_id] : r));

      if (!this.bulkHasFailures) {
        this.handleSuccess(this.$t("bulk_alarm_update.saved", { count: this.bulkResults.length }));
      }
    },

    retryBulkEmail() {
      return this.postBulkEmails();
    },
  },

  created() {
    this.modalWidth = window.innerWidth < MODAL_WIDTH ? window.innerWidth : MODAL_WIDTH;
    // Safe-4: ask the server which alarm central is Safe-4. Fails quietly
    // (no Safe-4 behavior) for roles without access or when not configured.
    this.axios
      .get(`${process.env.VUE_APP_SERVER_URL}/safe4/config`)
      .then((res) => { this.safe4_center_id = res.data.safe4_alarm_center_id || ""; })
      .catch(() => { this.safe4_center_id = ""; });
  },
};
</script>

<style>
/* Light-yellow tint so bulk update mode is unmistakable (SEC-218 v2).
   vue-js-modal renders to <body>, so this must be a global (unscoped) rule. */
.bulk-update-tint {
  background-color: #fffbe6 !important;
}
/* .modal-footer is position:absolute with no background and overlaps the
   modal-body's bottom padding, so the yellow body would show through it.
   Keep this modal's footer solid white, like every other modal. */
.modal-update-alarm-contract .modal-footer {
  background-color: #ffffff;
}
</style>
